/**
 * NBPLCClimateSnapshot — Climate & Circularity view
 * Route: /corporate/nbplc/climate
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { ChevronRight } from "lucide-react";

export default function NBPLCClimateSnapshot() {
  const navigate = useNavigate();
  const { consolidated, loading, selectedPeriod, period, periods, setSelectedPeriod } = useNBPLC();
  const [selectedBu, setSelectedBu] = useState(null);

  const byBu = consolidated?.by_bu || [];
  const totals = consolidated?.totals || {};
  const s1 = totals.scope1 || 0; const s2 = totals.scope2 || 0; const s3 = totals.scope3 || 0;
  const total = totals.total || 0;

  if (loading && !consolidated) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  const activeBu = selectedBu ? byBu.find(b=>b.bu_id===selectedBu) : null;
  const display = activeBu || (byBu[0] || {});

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Climate & Circularity" />
          <p className="text-xs text-slate-400 mt-0.5">{period?.label}</p>
        </div>
        <div className="flex gap-2">
          <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <select value={selectedBu||""} onChange={e=>setSelectedBu(e.target.value||null)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
            <option value="">All Plants</option>
            {byBu.map(b=><option key={b.bu_id} value={b.bu_id}>{b.bu_name.replace("NB ","")}</option>)}
          </select>
        </div>
      </div>

      {/* Corporate scope bars */}
      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">
          Scope Breakdown {activeBu ? `— ${activeBu.bu_name}` : "— All Plants"}
        </p>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[["Scope 1 (Fuels+Refrig.)", s1, "border-orange-400"],
            ["Scope 2 (Electricity)", s2, "border-blue-400"],
            ["Scope 3 (Packaging+Transport)", s3, "border-slate-400"]].map(([l,v,c]) => (
            <div key={l} className={`bg-slate-50 border-t-2 ${c} rounded-xl p-3`}>
              <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-1">{l}</p>
              <p className="text-lg font-bold text-slate-900">{Number(v/1000).toFixed(0)}K</p>
              <p className="text-[9px] text-slate-400">tCO₂e/month · {total>0?((v/total)*100).toFixed(0):0}%</p>
            </div>
          ))}
        </div>
        {[["Scope 1 — Direct Fuels", s1, "#f97316"],["Scope 2 — Grid Electricity", s2, "#3b82f6"],["Scope 3 — Packaging+Logistics", s3, "#64748b"]].map(([l,v,c])=>(
          <div key={l} className="bar-like mb-2">
            <div className="flex justify-between text-xs mb-0.5"><span className="text-slate-500">{l}</span><span className="font-semibold text-slate-700">{Number(v/1000).toFixed(1)}K tCO₂e</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{width:`${total>0?v/total*100:0}%`, background:c}}/>
            </div>
          </div>
        ))}
      </SectionCard>

      {/* Brewery KPIs */}
      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Brewery Intensity KPIs</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-100">
              {["Brewery","kgCO2e/hl","kWh/hl","hl water/hl","Renewable %","vs BIER Benchmark","Financial Impact"].map(h=>(
                <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-3 uppercase tracking-wide">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {byBu.map(bu => {
                const a = bu.assets?.[0] || {};
                const k = a.kpis || {};
                const ei = k.emission_intensity_kg_hl || 0;
                const en = k.energy_intensity_kwh_hl || 0;
                const wi = k.water_intensity_hl_hl || 0;
                const re = a.circularity?.renewable_pct || 0;
                const prod_hl = k.production_hl || 0;
                const bmark = ei <= 20 ? `✓ On target (${ei.toFixed(1)})` : `+${Math.round((ei-20)/20*100)}% above BIER (20)`;
                const bmarkCls = ei <= 20 ? "text-emerald-600" : ei <= 40 ? "text-amber-600" : "text-red-600";
                // Financial impact: 10% energy reduction = savings at $0.15/kWh
                const energySavings = Math.round(prod_hl * en * 0.10 * 0.15 * 12);
                const financialImpact = energySavings > 0 ? `-10% kWh/hl = $${(energySavings/1000).toFixed(0)}K/yr` : "—";
                return (
                  <tr key={bu.bu_id} className="border-b border-slate-50">
                    <td className="py-2 pr-3 font-semibold">{bu.bu_name.replace("NB ","")}</td>
                    <td className={`py-2 pr-3 font-bold tabular-nums ${ei<=20?"text-emerald-600":ei<=40?"text-amber-600":"text-red-600"}`}>{ei.toFixed(1)}</td>
                    <td className={`py-2 pr-3 tabular-nums ${en<=70?"text-emerald-600":"text-amber-600"}`}>{en.toFixed(0)}</td>
                    <td className={`py-2 pr-3 tabular-nums ${wi<=3.5?"text-emerald-600":"text-amber-600"}`}>{wi.toFixed(1)}</td>
                    <td className="py-2 pr-3">{re.toFixed(0)}%</td>
                    <td className={`py-2 pr-3 text-xs font-semibold ${bmarkCls}`}>{bmark}</td>
                    <td className="py-2 text-teal-700 font-semibold text-[10px]">{financialImpact}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[9px] text-slate-400 italic mt-2">Benchmarks: BIER 2022 — 20 kgCO₂e/hl (net-zero) · 70 kWh/hl · 3.5 hl/hl water</p>
      </SectionCard>

      {/* CTA */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-emerald-800">Top reduction levers identified</p>
          <p className="text-xs text-emerald-600 mt-0.5">Solar PV, Biomass Boiler, Returnable Bottles, Heat Recovery</p>
        </div>
        <button onClick={() => navigate("/corporate/nbplc/value-bridge")}
          className="px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 ml-4 whitespace-nowrap">
          View in Value Bridge →
        </button>
      </div>

      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Next Actions</p>
        {[["SDG Contribution",    "/corporate/nbplc/sdg"],
          ["Carbon Value Bridge", "/corporate/nbplc/value-bridge"],
          ["Carbon Strategy",     "/corporate/nbplc/carbon-strategy"]].map(([l,p]) => (
          <button key={p} onClick={() => navigate(p)}
            className="w-full flex items-center justify-between py-2 text-xs text-slate-600 hover:text-teal-700 group">
            {l} <ChevronRight size={11} className="text-slate-300 group-hover:text-teal-500"/>
          </button>
        ))}
      </SectionCard>
    </div>
  );
}
